import React, { useState } from 'react';
import { Button, Input, InputNumber, Row, Col, Space, ColorPicker, Select, Table } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { usePhaserConfig } from '../contexts/PhaserConfigContext';
import { TextObjectConfig } from '../types/config';


const { Option } = Select;

interface TextConfiguratorProps {
    // On reçoit textsConfig via ConfiguratorPanel, mais on s'appuie surtout sur le contexte
    textsConfig: TextObjectConfig[];
}

const fontOptions = [
    'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia', 'Comic Sans MS', 'Impact', 'Tahoma'
];

const TextConfigurator: React.FC<TextConfiguratorProps> = ({ textsConfig }) => {
    const { texts, addText, updateText, removeText } = usePhaserConfig();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentText, setCurrentText] = useState<TextObjectConfig | null>(null);

    /**
     * Affiche la liste des TextObjects sous forme de "cards" ou blocs,
     * chaque bloc proposant les actions Éditer/Supprimer
     */
    const renderTextTable = () => {
        const columns = [

            /* Nom + ID */
            {
                dataIndex: 'id',
                render: (_: any, txt: TextObjectConfig) => (
                    <div>
                        <strong>{txt.textString?.slice(0, 20) || '(Texte)'}</strong>
                        <div style={{ fontSize: 12, color: '#888' }}>ID : {txt.id}</div>
                    </div>
                ),
            },
            /* Actions */
            {
                key: 'actions',
                align: 'right' as const,
                width: 90,
                render: (_: any, txt: TextObjectConfig) => (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => {
                                setCurrentText(txt);
                                setIsEditing(true);
                            }}
                        />
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={() => removeText(txt.id)}
                        />
                    </Space>
                ),
            },
        ];

        return (
            <>
                {/* Bouton “Ajouter” aligné à droite */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setCurrentText(null);
                            setIsEditing(true);
                        }}
                    >
                        Ajouter
                    </Button>
                </div>

                <Table<TextObjectConfig>
                    columns={columns}
                    dataSource={texts}
                    rowKey="id"
                    pagination={false}
                    showHeader={false}  /* 🌱 Pas d’en‑tête */
                    size="small"        /* 🌱 Compacte */
                    bordered={false}    /* 🌱 Sans bordure */
                />
            </>
        );
    };


    /**
     * Gère la création ou la mise à jour du texte courant (currentText) dans le context
     */
    const handleSaveText = () => {
        if (!currentText) return;

        // Si l'id existe déjà dans notre tableau, on update
        const alreadyExists = texts.some((t) => t.id === currentText.id);

        if (alreadyExists) {
            updateText(currentText.id, currentText);
        } else {
            // Sinon on ajoute
            // On peut générer un nouvel ID si besoin
            const newText: TextObjectConfig = {
                ...currentText,
                id: currentText.id || crypto.randomUUID(),
            };
            addText(newText);
        }

        // Sortie du mode édition
        setIsEditing(false);
        setCurrentText(null);
    };

    /**
     * Formulaire d'édition ou d'ajout d'un TextObject
     */
    const renderForm = () => {
        return (
            <div style={{ marginTop: 16 }}>
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <label>Texte</label>
                        <Input
                            value={currentText?.textString}
                            onChange={(e) =>
                                setCurrentText((prev) => ({
                                    ...prev!,
                                    textString: e.target.value,
                                }))
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <label>Font Family</label>
                        <Select
                            value={currentText?.fontFamily || 'Arial'}
                            onChange={(value) => setCurrentText((prev) => ({ ...prev!, fontFamily: value }))}
                            style={{ width: '100%' }}
                        >
                            {fontOptions.map((font) => (
                                <Option key={font} value={font}>{font}</Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <label>Font Size</label>
                        <InputNumber
                            value={currentText?.fontSize}
                            min={1}
                            max={200}
                            style={{ width: '100%' }}
                            onChange={(val) =>
                                setCurrentText((prev) => ({
                                    ...prev!,
                                    fontSize: val || 16,
                                }))
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <label>Couleur du texte</label>
                        <ColorPicker
                            format="hex"
                            value={currentText?.color || '#000000'}
                            onChange={(color) => {
                                setCurrentText((prev) => ({
                                    ...prev!,
                                    color: color.toHexString(),
                                }));
                            }}
                            style={{ width: '100%' }}  // Ajout de cette ligne
                        />
                    </Col>

                </Row>

                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <label>Couleur de fond</label>
                        <ColorPicker
                            format="hex"
                            value={currentText?.backgroundColor || '#ffffff'}
                            onChange={(color) => {
                                setCurrentText((prev) => ({
                                    ...prev!,
                                    backgroundColor: color.toHexString(),
                                }));
                            }}
                            style={{ width: '100%' }}  // Ajout de cette ligne

                        />
                    </Col>

                    <Col span={6}>
                        <label>Position X</label>
                        <InputNumber
                            value={currentText?.x}
                            min={0}
                            max={2000}
                            style={{ width: '100%' }}
                            onChange={(val) =>
                                setCurrentText((prev) => ({
                                    ...prev!,
                                    x: val ?? 0,
                                }))
                            }
                        />
                    </Col>
                    <Col span={6}>
                        <label>Position Y</label>
                        <InputNumber
                            value={currentText?.y}
                            min={0}
                            max={2000}
                            style={{ width: '100%' }}
                            onChange={(val) =>
                                setCurrentText((prev) => ({
                                    ...prev!,
                                    y: val ?? 0,
                                }))
                            }
                        />
                    </Col>
                </Row>

                {/* Boutons Enregistrer / Annuler */}
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <Button type="primary" onClick={handleSaveText}>
                        Enregistrer
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={() => setIsEditing(false)}>
                        Annuler
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div>
            {isEditing ? renderForm() : renderTextTable()}
        </div>
    );
};

export default TextConfigurator;
